import React from 'react';
import Link from 'next/link';
import ReactPrismic from 'prismic-reactjs';

import { BlogPostDocument } from '@/domains/blog';
import { urls } from '@/constants';

import Seo from '@/components/Seo';
import Container from '@/components/Container';
import Typography from '@/components/Typography';
import Spacer from '@/components/Spacer';
import ActiveLink from '@/components/ActiveLink';
import AspectRatio from '@/components/AspectRatio';
import FormattedDate from '@/components/FormattedDate';
import { Tags, Tag, TagContent } from '@/components/Tag';

import style from './BlogPage.sass';
import Content from '@/components/Content';

interface PartialBlogPostDocument extends Pick<BlogPostDocument, 'id' | 'uid'> {
  title: BlogPostDocument['data']['title'];
  description: BlogPostDocument['data']['description'];
  image: BlogPostDocument['data']['image'];
  publishedAt: BlogPostDocument['data']['published_at'];
  tags: BlogPostDocument['tags'];
}

export interface Query {
  [x: string]: any;
  tag: string;
}

export interface Props {
  tags: string[];
  documents: PartialBlogPostDocument[];
}

const BlogPage: React.FC<Props> = ({ tags, documents }) => (
  <React.Fragment>
    <Seo
      title="Blog"
      description="I get excited by topics like serverless, automation and infrastructure. If you feel the same, just follow me."
    />

    <Container>
      <Content>
        <Spacer xs={8} />

        <Spacer xs={4} type="margin">
          <Typography is="h1" type="h1" underline>
            Blog
          </Typography>
        </Spacer>

        <Spacer xs={4}>
          <div>
            <Tags>
              <Tag>
                <ActiveLink exact href={urls.blog} passHref activeClassName={style.active}>
                  <TagContent is={(props: any) => <Typography is="a" {...props} />} className={style.tag}>
                    all
                  </TagContent>
                </ActiveLink>
              </Tag>

              {tags.map((item) => (
                <Tag key={item}>
                  <ActiveLink {...urls.blogCategory(item)} passHref activeClassName={style.active}>
                    <TagContent is={(props: any) => <Typography is="a" {...props} />} className={style.tag}>
                      {item}
                    </TagContent>
                  </ActiveLink>
                </Tag>
              ))}
            </Tags>
          </div>
        </Spacer>

        <div className={style.grid}>
          {documents.map((document) => (
            <article key={document.id} className={style.item}>
              <Link {...urls.blogPost(document.uid)}>
                <a className={style.link}>
                  <Spacer xs={2}>
                    <div className={style.imageContainer}>
                      <AspectRatio
                        is="div"
                        ratio={document.image.thumbnail.dimensions.width / document.image.thumbnail.dimensions.height}
                      >
                        <img
                          src={document.image.thumbnail.url}
                          alt={document.image.thumbnail.alt}
                          className={style.image}
                        />
                      </AspectRatio>
                    </div>
                  </Spacer>

                  <header>
                    <Spacer xs={2}>
                      <Typography is="div" type="h3">
                        {ReactPrismic.RichText.asText(document.title)}
                      </Typography>
                    </Spacer>
                  </header>

                  <Spacer xs={3}>
                    <div>
                      <Typography is="p" className={style.description}>
                        {ReactPrismic.RichText.asText(document.description)}
                      </Typography>
                    </div>
                  </Spacer>

                  <section>
                    <Tags>
                      {document.tags.map((tag) => (
                        <Tag key={tag}>
                          <TagContent is={Typography}>{tag}</TagContent>
                        </Tag>
                      ))}

                      <Tag>
                        <TagContent is={Typography}>
                          <FormattedDate date={document.publishedAt} />
                        </TagContent>
                      </Tag>
                    </Tags>
                  </section>
                </a>
              </Link>
            </article>
          ))}
        </div>

        <Spacer xs={16} />
      </Content>
    </Container>
  </React.Fragment>
);

export default BlogPage;
